import {useEffect, useState} from 'react';
import Title from 'antd/es/typography/Title';

import {
  Button,
  Checkbox,
  Col,
  Input,
  InputNumber,
  Row,
  Select,
  Typography,
} from 'antd';
import {useHistory} from 'react-router-dom';
import {Program} from '../../graphql/API';
import {PackagesService} from './PackagesService';
import {ProgramsService} from '../Program';
import type {
  Currency,
  NewPackageType,
} from '@PikaDoApp/npm-pikado-logic/dist/modules/package/graphql/package.interface';

const packagesService = new PackagesService();
const programService = new ProgramsService();

export const PackageCreate = () => {
  const history = useHistory();
  const {Text} = Typography;

  const [isError, setIsError] = useState<boolean>(false);
  const [programs, setPrograms] = useState<Array<Program> | []>();

  // Initialize with default values
  const [packageData, setPackageData] = useState({
    name_i18n: {en: '', ar: '', tr: ''},
    description_i18n: {en: '', ar: '', tr: ''},
    benefits_i18n: {en: '', ar: '', tr: ''},
    programId: '',
    packageDuration: 1,
    type: 'STUDENT' as NewPackageType,
    onlyAI: false,
    isFree: false,
    isActive: false,
    isLoadTest: false,
    productIAP: '',
    price: {
      US: {price: 0, currency: 'USD' as Currency},
    },
  });

  useEffect(() => {
    programService
      .getPrograms()
      .then((e) => {
        setPrograms(e);
      })
      .catch((err) => {
        console.log('Programs fetching error ->', err);
      });
  }, []);

  const onChange = (prop: string, value: any) => {
    setPackageData({...packageData, [prop]: value});
  };

  const onChangeI18n = (
    field: 'name_i18n' | 'description_i18n' | 'benefits_i18n',
    lang: 'en' | 'ar' | 'tr',
    value: string,
  ) => {
    setPackageData({
      ...packageData,
      [field]: {
        ...packageData[field],
        [lang]: value,
      },
    });
  };

  const onChangePrice = (
    country: 'US' | 'AE' | 'EG' | 'SA' | 'QA' | 'KW' | 'TR',
    field: 'price' | 'currency',
    value: number | Currency,
  ) => {
    const currentPrice = packageData.price || {} as any;
    const countryPrice = currentPrice[country] || {price: 0, currency: 'USD' as Currency};

    setPackageData({
      ...packageData,
      price: {
        ...currentPrice,
        [country]: {
          ...countryPrice,
          [field]: value,
        },
      },
    });
  };

  const handleCreate = async () => {
    try {
      // Validate required fields
      if (!packageData.name_i18n.en) {
        alert('Please enter a package name (English)');
        return;
      }

      if (!packageData.programId) {
        alert('Please select a program');
        return;
      }

      if (!packageData.price.US || packageData.price.US.price === 0) {
        alert('Please set US price');
        return;
      }

      const createInput = {
        packageDuration: packageData.packageDuration,
        programId: packageData.programId,
        description_i18n: packageData.description_i18n,
        name_i18n: packageData.name_i18n,
        benefits_i18n: packageData.benefits_i18n,
        onlyAI: packageData.onlyAI,
        type: packageData.type,
        price: packageData.price,
        isLoadTest: packageData.isLoadTest,
        isFree: packageData.isFree,
        isActive: packageData.isActive,
        productIAP: packageData.productIAP || undefined,
      };

      console.log('Creating package with:', createInput);
      const result = await packagesService.createPackage(createInput);
      console.log('Package created:', result);
      alert('Package created successfully');
      history.push('/packages');
    } catch (err: any) {
      alert('Package creation failed: ' + (err.message || 'Unknown error'));
      console.error('Create error:', err);
    }
  };

  return (
    <div style={{padding: 10}}>
      <Title style={{textAlign: 'center'}}>Create New Package</Title>

      {/* Basic Information */}
      <Row gutter={16}>
        <Col span={12}>
          <div style={{margin: 20}}>
            <Text style={{display: 'block'}} strong={true}>
              Package Name (English)*
            </Text>
            <Input
              value={packageData.name_i18n.en}
              onChange={(e) => onChangeI18n('name_i18n', 'en', e.target.value)}
            />
          </div>
        </Col>
        <Col span={12}>
          <div style={{margin: 20}}>
            <Text style={{display: 'block'}} strong={true}>
              Package Name (Arabic)
            </Text>
            <Input
              value={packageData.name_i18n.ar}
              onChange={(e) => onChangeI18n('name_i18n', 'ar', e.target.value)}
            />
          </div>
        </Col>
      </Row>

      {/* Description */}
      <Row gutter={16}>
        <Col span={12}>
          <div style={{margin: 20}}>
            <Text style={{display: 'block'}} strong={true}>
              Description (English)
            </Text>
            <Input.TextArea
              rows={3}
              value={packageData.description_i18n.en}
              onChange={(e) => onChangeI18n('description_i18n', 'en', e.target.value)}
            />
          </div>
        </Col>
        <Col span={12}>
          <div style={{margin: 20}}>
            <Text style={{display: 'block'}} strong={true}>
              Description (Arabic)
            </Text>
            <Input.TextArea
              rows={3}
              value={packageData.description_i18n.ar}
              onChange={(e) => onChangeI18n('description_i18n', 'ar', e.target.value)}
            />
          </div>
        </Col>
      </Row>

      {/* Benefits */}
      <Row gutter={16}>
        <Col span={12}>
          <div style={{margin: 20}}>
            <Text style={{display: 'block'}} strong={true}>
              Benefits (English)
            </Text>
            <Input.TextArea
              rows={3}
              value={packageData.benefits_i18n.en}
              onChange={(e) => onChangeI18n('benefits_i18n', 'en', e.target.value)}
            />
          </div>
        </Col>
        <Col span={12}>
          <div style={{margin: 20}}>
            <Text style={{display: 'block'}} strong={true}>
              Benefits (Arabic)
            </Text>
            <Input.TextArea
              rows={3}
              value={packageData.benefits_i18n.ar}
              onChange={(e) => onChangeI18n('benefits_i18n', 'ar', e.target.value)}
            />
          </div>
        </Col>
      </Row>

      {/* Program and Duration */}
      <Row gutter={16}>
        <Col span={12}>
          <div style={{margin: 20}}>
            <Text style={{display: 'block'}} strong={true}>
              Program*
            </Text>
            <Select
              value={packageData.programId || undefined}
              showSearch
              style={{width: '100%'}}
              placeholder="Select a program"
              optionFilterProp="children"
              onChange={(e) => onChange('programId', e)}>
              {programs?.map((program) => {
                const programName = typeof program.name_i18n === 'string'
                  ? JSON.parse(program.name_i18n)['en']
                  : program.name_i18n?.en || program.id;
                return (
                  <Select.Option key={program.id} value={program.id}>
                    {programName}
                  </Select.Option>
                );
              })}
            </Select>
          </div>
        </Col>
        <Col span={12}>
          <div style={{margin: 20}}>
            <Text style={{display: 'block'}} strong={true}>
              Package Duration (months)*
            </Text>
            <InputNumber
              min={1}
              style={{width: '100%'}}
              value={packageData.packageDuration}
              onChange={(value) => onChange('packageDuration', value)}
            />
          </div>
        </Col>
      </Row>

      {/* Package Type and Product IAP */}
      <Row gutter={16}>
        <Col span={12}>
          <div style={{margin: 20}}>
            <Text style={{display: 'block'}} strong={true}>
              Package Platform Type*
            </Text>
            <Select
              value={packageData.type}
              style={{width: '100%'}}
              onChange={(value) => onChange('type', value)}>
              <Select.Option value="STUDENT">Student</Select.Option>
              <Select.Option value="BUSINESS">Business</Select.Option>
            </Select>
          </div>
        </Col>
        <Col span={12}>
          <div style={{margin: 20}}>
            <Text style={{display: 'block'}} strong={true}>
              Product IAP
            </Text>
            <Input
              value={packageData.productIAP}
              onChange={(e) => onChange('productIAP', e.target.value)}
            />
          </div>
        </Col>
      </Row>

      {/* Checkboxes */}
      <Row gutter={16}>
        <Col span={6}>
          <div style={{margin: 20}}>
            <Checkbox
              checked={packageData.onlyAI}
              onChange={(e) => onChange('onlyAI', e.target.checked)}>
              Only AI
            </Checkbox>
          </div>
        </Col>
        <Col span={6}>
          <div style={{margin: 20}}>
            <Checkbox
              checked={packageData.isFree}
              onChange={(e) => onChange('isFree', e.target.checked)}>
              Free Package
            </Checkbox>
          </div>
        </Col>
        <Col span={6}>
          <div style={{margin: 20}}>
            <Checkbox
              checked={packageData.isActive}
              onChange={(e) => onChange('isActive', e.target.checked)}>
              Active
            </Checkbox>
          </div>
        </Col>
        <Col span={6}>
          <div style={{margin: 20}}>
            <Checkbox
              checked={packageData.isLoadTest}
              onChange={(e) => onChange('isLoadTest', e.target.checked)}>
              Load Test
            </Checkbox>
          </div>
        </Col>
      </Row>

      {/* Pricing Section */}
      <Title level={4} style={{marginTop: 20}}>Pricing</Title>

      {/* US Price (Required) */}
      <Row gutter={16}>
        <Col span={12}>
          <div style={{margin: 20}}>
            <Text style={{display: 'block'}} strong={true}>
              US Price*
            </Text>
            <InputNumber
              min={0}
              style={{width: '100%'}}
              value={packageData.price.US.price}
              onChange={(value) => onChangePrice('US', 'price', value || 0)}
            />
          </div>
        </Col>
        <Col span={12}>
          <div style={{margin: 20}}>
            <Text style={{display: 'block'}} strong={true}>
              US Currency*
            </Text>
            <Select
              value={packageData.price.US.currency}
              style={{width: '100%'}}
              onChange={(value) => onChangePrice('US', 'currency', value as Currency)}>
              <Select.Option value="USD">USD</Select.Option>
              <Select.Option value="AED">AED</Select.Option>
              <Select.Option value="EGP">EGP</Select.Option>
              <Select.Option value="SAR">SAR</Select.Option>
              <Select.Option value="QAR">QAR</Select.Option>
              <Select.Option value="KWD">KWD</Select.Option>
              <Select.Option value="TRY">TRY</Select.Option>
            </Select>
          </div>
        </Col>
      </Row>

      {/* Additional Country Prices (Optional) */}
      {['AE', 'EG', 'SA', 'QA', 'KW', 'TR'].map((country) => (
        <Row key={country} gutter={16}>
          <Col span={12}>
            <div style={{margin: 20}}>
              <Text style={{display: 'block'}} strong={true}>
                {country} Price (Optional)
              </Text>
              <InputNumber
                min={0}
                style={{width: '100%'}}
                value={packageData.price?.[country as keyof typeof packageData.price]?.price}
                onChange={(value) => onChangePrice(country as any, 'price', value || 0)}
              />
            </div>
          </Col>
          <Col span={12}>
            <div style={{margin: 20}}>
              <Text style={{display: 'block'}} strong={true}>
                {country} Currency
              </Text>
              <Select
                value={packageData.price?.[country as keyof typeof packageData.price]?.currency || 'USD'}
                style={{width: '100%'}}
                onChange={(value) => onChangePrice(country as any, 'currency', value as Currency)}>
                <Select.Option value="USD">USD</Select.Option>
                <Select.Option value="AED">AED</Select.Option>
                <Select.Option value="EGP">EGP</Select.Option>
                <Select.Option value="SAR">SAR</Select.Option>
                <Select.Option value="QAR">QAR</Select.Option>
                <Select.Option value="KWD">KWD</Select.Option>
                <Select.Option value="TRY">TRY</Select.Option>
              </Select>
            </div>
          </Col>
        </Row>
      ))}

      <Row>
        <Col span={24}>
          <div style={{margin: 20, textAlign: 'center'}}>
            <Button type="primary" size="large" onClick={handleCreate}>
              Create Package
            </Button>
          </div>
        </Col>
      </Row>

      {isError && <Title>Error ⚠️</Title>}
    </div>
  );
};
